import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContactApiError,
  submitContactForm,
} from "../../services/contactService";
import {
  hasValidationErrors,
  validateContactForm,
} from "../../utils/contactValidation";

const MAX_LENGTHS = {
  name: 100,
  email: 254,
  phoneNumber: 20,
  message: 2000,
};

const INITIAL_FORM = {
  name: "",
  email: "",
  phoneNumber: "",
  message: "",
};

function ContactForm() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const max = MAX_LENGTHS[name];
    setFormData((prev) => ({
      ...prev,
      [name]: max ? value.slice(0, max) : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    if (submitError) setSubmitError("");
    if (submitSuccess) setSubmitSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");

    const validationErrors = validateContactForm(formData);
    if (hasValidationErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await submitContactForm(formData);

      setFormData(INITIAL_FORM);
      setSubmitSuccess(
        response.message || t("Your message has been sent successfully."),
      );
    } catch (err) {
      const message =
        err instanceof ContactApiError
          ? err.message
          : t("Failed to send message. Please try again.");
      setSubmitError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex w-full items-center justify-center px-2 md:max-w-md">
      <div className="caf-form-panel">
        <form onSubmit={handleSubmit} noValidate>
          <div className="mb-4">
            <label htmlFor="name" className="caf-field-label">
              {t("Name")}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              autoComplete="name"
              maxLength={MAX_LENGTHS.name}
              className={`caf-input${errors.name ? " caf-input--error" : ""}`}
              disabled={loading}
            />
            {errors.name ? (
              <span className="caf-field-error">{t(errors.name)}</span>
            ) : null}
          </div>
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="mb-4 md:flex-1">
              <label htmlFor="email" className="caf-field-label">
                {t("Email")}
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                autoComplete="email"
                maxLength={MAX_LENGTHS.email}
                className={`caf-input${errors.email ? " caf-input--error" : ""}`}
                disabled={loading}
              />
              {errors.email ? (
                <span className="caf-field-error">{t(errors.email)}</span>
              ) : null}
            </div>
            <div className="mb-4 md:flex-1">
              <label htmlFor="phoneNumber" className="caf-field-label">
                {t("Phone Number")}
              </label>
              <input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                autoComplete="tel"
                maxLength={MAX_LENGTHS.phoneNumber}
                className="caf-input"
                disabled={loading}
              />
            </div>
          </div>
          <div className="mb-5">
            <label htmlFor="message" className="caf-field-label">
              {t("Message")}
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              maxLength={MAX_LENGTHS.message}
              rows="5"
              className={`caf-input resize-y${errors.message ? " caf-input--error" : ""}`}
              disabled={loading}
            />
            {errors.message ? (
              <span className="caf-field-error">{t(errors.message)}</span>
            ) : null}
          </div>

          {submitSuccess ? (
            <p className="caf-form-message caf-form-message--success" role="status">
              {submitSuccess}
            </p>
          ) : null}

          {submitError ? (
            <p className="caf-form-message caf-form-message--error" role="alert">
              {submitError}
            </p>
          ) : null}

          <button
            type="submit"
            className="caf-btn caf-btn--primary caf-btn--block"
            disabled={loading}
          >
            {loading ? t("Sending...") : t("Send")}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;
